import { Routes } from "@angular/router";
import { authGuard } from "./guards/auth.guard";
import { attendanceProductGuard, isProductActiveGuard } from "./guards/is-product-active.guard";
import { roleGuard } from "./guards/role.guard";
import {
  ADMIN_ROLES,
  ORGANIZATION_SETTINGS_ROLES,
  NON_EMPLOYEE_NON_MANAGER_ROLES,
  NON_EMPLOYEE_ROLES,
  Role,
} from "./pages/auth/model/role.model";
import { loadUserGuard } from "./guards/load-user.guard";
import { administrationGuard } from "./guards/administration.guard";
import { organizationCapabilityGuard } from "./guards/organization-capability.guard";
import { myPayGuard } from "./pages/my-payroll/my-payroll.guard";
import { permissionGuard } from "./guards/permission.guard";
import { organizationSettingsAccessGuard, organizationSettingsProductGuard } from "./guards/organization-settings-access.guard";
import { employeePerformanceRoute } from "./pages/employee/performance/employee-performance.route";
import { financialsRoute } from "./pages/financials/financials.route";

export const routes: Routes = [
  {
    path: "",
    canActivate: [authGuard],
    canActivateChild: [loadUserGuard],
    children: [
      { path: "", pathMatch: "full", redirectTo: "home" },
      {
        path: "home",
        loadComponent: () =>
          import("./pages/home/home.component").then((m) => m.HomeComponent),
        data: { breadcrumb: "home" },
        children: [
          { path: "", pathMatch: "full", redirectTo: "dashboard" },
          employeePerformanceRoute,
          financialsRoute,
          {
            path: "dashboard",
            loadComponent: () =>
              import("./pages/dashboard/components/home/home.component").then(
                (r) => r.HomeComponent,
              ),
            canActivate: [isProductActiveGuard("CoreHR")],
          },
          {
            path: "profile",
            data: { breadcrumb: "profile" },
            loadChildren: () =>
              import("./pages/profile/profile.routes").then((r) => r.routes),
          },
          {
            path: "organization-settings",
            loadChildren: () =>
              import("./pages/organization-settings/organization-settings.routes").then(
                (r) => r.routes,
              ),
            canActivate: [organizationSettingsProductGuard, organizationSettingsAccessGuard],
            canActivateChild: [organizationSettingsAccessGuard],
            data: { roles: ORGANIZATION_SETTINGS_ROLES, breadcrumb: "settings" },
          },
          {
            path: "employees",
            loadChildren: () =>
              import("./pages/employees/employees.route").then((r) => r.routes),
            canActivate: [isProductActiveGuard("CoreHR"), roleGuard],
            data: {
              roles: [
                "Admin",
                "GlobalAdmin",
                "SupportAdmin",
                "Manager",
                "HR",
              ] satisfies Role[],
              breadcrumb: "employees",
            },
          },
          {
            path: "calendar",
            data: { breadcrumb: "calendar" },
            loadChildren: () =>
              import("./pages/calendar/calendar.route").then((r) => r.routes),
          },
          {
            path: "payroll",
            data: { breadcrumb: "payroll" },
            loadChildren: () =>
              import("./pages/payroll/payroll.routes").then((r) => r.routes),
            canActivate: [permissionGuard("payroll.manage"), organizationCapabilityGuard("payroll")],
          },
          {
            path: "subscription",
            loadChildren: () =>
              import("./pages/subscription/subscription.route").then(
                (r) => r.routes,
              ),
            canActivate: [roleGuard],
            data: {
              roles: ADMIN_ROLES,
              breadcrumb: "subscription",
            },
          },
          {
            path: "recruitment",
            loadChildren: () =>
              import("./pages/recruitment/recuitment.routes").then(
                (r) => r.routes,
              ),
            canActivate: [roleGuard],
            data: {
              roles: NON_EMPLOYEE_ROLES,
              breadcrumb: "recruitment",
            },
          },
          {
            path: "administration",
            loadChildren: () =>
              import("./pages/administration/administration.routes").then(
                (r) => r.routes,
              ),
            canActivate: [administrationGuard],
            data: { breadcrumb: "administration" },
          },
          {
            path: "help",
            loadComponent: () =>
              import("./pages/help/help.component").then(
                (m) => m.HelpComponent,
              ),
            data: { breadcrumb: "help" },
          },
          {
            path: "ai",
            pathMatch: "full",
            redirectTo: "dashboard",
          },
          {
            path: "tasks",
            loadChildren: () =>
              import("./pages/task/task.route").then((r) => r.routes),
            canActivate: [isProductActiveGuard("CoreHR")],
            data: { breadcrumb: "tasks" },
          },
          {
            path: "reports",
            loadChildren: () =>
              import("./pages/reports/reports.route").then((r) => r.routes),
            data: { breadcrumb: "reports", roles: NON_EMPLOYEE_NON_MANAGER_ROLES },
            canActivate: [roleGuard],
          },
          {
            path: "surveys",
            loadChildren: () =>
              import("./pages/survey/survey.route").then((r) => r.routes),
            canActivate: [isProductActiveGuard("Surveys")],
            data: { breadcrumb: "surveys" },
          },
          {
            path: "announcements",
            data: { breadcrumb: "announcements" },
            loadChildren: () =>
              import("./pages/announcement/announcement.route").then(
                (r) => r.routes,
              ),
          },
          {
            path: "organization-chart",
            data: { breadcrumb: "organization chart" },
            loadChildren: () =>
              import("./pages/organization-chart/organization-chart.route").then(
                (r) => r.routes,
              ),
            canActivate: [isProductActiveGuard("CoreHR")],
          },
          {
            path: "attendance",
            loadChildren: () =>
              import("./pages/attendance/attendance.routes").then(
                (r) => r.routes,
              ),
            canActivate: [attendanceProductGuard],
          },
          {
            path: "employee",
            loadChildren: () =>
              import("./pages/employee/employee.route").then((m) => m.routes),
          },
          {
            path: "my-payroll",
            loadChildren: () =>
              import("./pages/my-payroll/my-payroll.routes").then(
                (m) => m.routes,
              ),
            data: { breadcrumb: "My Pay" },
            canActivate: [permissionGuard("payroll.access"), organizationCapabilityGuard("payroll"), myPayGuard],
          },
          {
            path: "403-forbidden",
            loadComponent: () =>
              import("./pages/forbidden/forbidden.component").then(
                (c) => c.ForbiddenComponent,
              ),
          },
        ],
      },
      {
        path: "company-setup",
        data: { roles: ADMIN_ROLES },
        canActivate: [roleGuard],
        loadChildren: () =>
          import("./pages/company-setup/company-setup.routes").then(
            (r) => r.routes,
          ),
      },
      {
        path: "kiosk/:workZoneId",
        loadComponent: () =>
          import("./pages/organization-settings/components/attendance/kiosk-view/kiosk-view.component").then(
            (c) => c.KioskViewComponent,
          ),
      },
    ],
  },
  {
    path: "auth",
    loadChildren: () => import("./pages/auth/auth.routes").then((r) => r.route),
  },
  {
    path: "privacy-policy",
    loadComponent: () =>
      import("./pages/privacy-policy/privacy-policy.component").then(
        (c) => c.PrivacyPolicyComponent,
      ),
  },
  {
    path: "terms-of-use",
    loadComponent: () =>
      import("./pages/terms-of-use/terms-of-use.component").then(
        (c) => c.TermsOfUseComponent,
      ),
  },
  {
    path: "subscription-terms",
    loadComponent: () =>
      import("./pages/subscription-terms/subscription-terms.component").then(
        (c) => c.SubscriptionTermsComponent,
      ),
  },
  {
    path: "documentsupload/upload",
    loadComponent: () =>
      import("./pages/anonymous-document-upload/anonymous-document-upload.component").then(
        (m) => m.AnonymousDocumentUploadComponent,
      ),
  },
  {
    path: "sign",
    loadComponent: () =>
      import("./pages/common/document-automation/document-signature-page.component").then(
        (m) => m.DocumentSignaturePageComponent,
      ),
  },
  {
    path: "careers",
    loadChildren: () =>
      import("./pages/recruitment/career.routes").then((c) => c.careerRoutes),
  },
];
